export { COOKIE_NAME, ONE_YEAR_MS } from "@shared/const";

const OAUTH_STATE_COOKIE = "__Host-oauth_state";

/**
 * Encode OAuth state: base64url of JSON { redirectUri, nonce, returnPath? }
 */
function encodeOAuthState(redirectUri: string, nonce: string, returnPath?: string): string {
  return btoa(JSON.stringify({ redirectUri, nonce, ...(returnPath ? { returnPath } : {}) }));
}

/**
 * Decode OAuth state safely — never throws.
 * Returns { redirectUri, nonce } or empty strings on malformed input.
 */
export function decodeOAuthState(state: string): { redirectUri: string; nonce: string } {
  try {
    const decoded = JSON.parse(atob(state));
    return {
      redirectUri: typeof decoded.redirectUri === "string" ? decoded.redirectUri : "",
      nonce: typeof decoded.nonce === "string" ? decoded.nonce : "",
    };
  } catch {
    return { redirectUri: "", nonce: "" };
  }
}

/**
 * Generate the Manus OAuth login URL.
 * Uses window.location.origin so it works on any domain (dev, preview, production).
 * Sets a __Host- CSRF nonce cookie to prevent session-fixation attacks.
 * MUST only be called in a click/navigation handler — never on render.
 *
 * @param returnPath - Optional path to redirect to after successful login (e.g. "/join/abc123").
 *   Encoded into the OAuth state so it survives the full OAuth round-trip.
 */
export const getLoginUrl = (returnPath?: string) => {
  const oauthPortalUrl = import.meta.env.VITE_OAUTH_PORTAL_URL;
  const appId = import.meta.env.VITE_APP_ID;
  const redirectUri = `${window.location.origin}/api/oauth/callback`;

  // Mint a one-time nonce and store it in a host-only cookie (expires in 10 min)
  const nonce = crypto.randomUUID();
  document.cookie = `${OAUTH_STATE_COOKIE}=${nonce}; Path=/; Max-Age=600; SameSite=None; Secure`;

  const state = encodeOAuthState(redirectUri, nonce, returnPath);

  const params = new URLSearchParams({
    app_id: appId,
    redirect_url: redirectUri,
    state,
  });

  return `${oauthPortalUrl}/login?${params.toString()}`;
};

export { OAUTH_STATE_COOKIE };
